import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Skeleton, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import useMonthlyTrends from "../../../hooks/pearli/useMonthlyTrends";
import DomainTrendChart from "./DomainTrendChart";
import TrendsOverview from "./TrendsOverview";
import { STATUS, rangeText } from "./trendDisplay";

const METHOD = {
    personal: [
        "Your monthly assessment averages.",
        "Each month averages your saved assessment records within that calendar month. No observation is treated as zero.",
    ],
    employer: [
        "Employer cohort monthly averages.",
        "Average each participant’s records within the month, then weight contributing participants equally. Monthly cohorts can change: a rise or fall in group averages does not establish improvement or deterioration in the same people.",
    ],
    free: [
        "Free-assessment cohort monthly averages.",
        "Average each participant’s records within the month, then weight contributing participants equally. Monthly cohorts can change: a rise or fall in group averages does not establish improvement or deterioration in the same people.",
    ],
};

const LEGEND = [
    ["good", STATUS.good],
    ["warning", STATUS.warning],
    ["attention", STATUS.attention],
];

function Legend() {
    return (
        <Box
            component="ul"
            aria-label="Graph key"
            sx={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", m: 0, p: "0 0 4px", listStyle: "none", fontSize: 12, color: "#334155" }}
        >
            {LEGEND.map(([key, status]) => (
                <Box component="li" key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box component="span" sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: status.color }} />
                    {status.label}
                </Box>
            ))}
            <Box component="li" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box component="span" sx={{ width: 18, borderTop: "1.5px dashed #475569" }} />
                3.2 benchmark
            </Box>
        </Box>
    );
}

/**
 * PEARLI monthly trends for one audience: six status-coloured domain graphs in one row, then the
 * AI Well-Being Overview. Domain names in the overview highlight their graph.
 *
 * @param {"personal"|"employer"|"free"} audience
 * @param {string} endpoint  monthly-trends endpoint; `${endpoint}/overview` serves the overview
 * @param {object} params    start_month, end_month and the audience's filters
 */
export default function PearliMonthlyTrends({ audience, endpoint, params, enabled = true }) {
    const { data, loading, error, overview, overviewLoading } = useMonthlyTrends(endpoint, params, enabled);
    const [preview, setPreview] = useState(null);
    const [pinned, setPinned] = useState(null);
    const [overflowing, setOverflowing] = useState(false);
    const scrollRef = useRef(null);
    const cardRefs = useRef({});

    const overviewKey = data?.overviewKey;
    useEffect(() => {
        setPinned(null);
        setPreview(null);
    }, [overviewKey]);

    useEffect(() => {
        const node = scrollRef.current;
        if (!node) return undefined;
        const measure = () => setOverflowing(node.scrollWidth > node.clientWidth + 2);
        measure();
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [data]);

    const pin = useCallback((id) => setPinned((current) => (current === id ? null : id)), []);

    // A pinned domain's graph is centred in its row, then the page scrolls only as far as needed, so
    // containers outside the row never jump sideways.
    useEffect(() => {
        const card = pinned && cardRefs.current[pinned];
        const row = scrollRef.current;
        if (!card?.scrollIntoView) return;
        if (row) {
            const offset = card.getBoundingClientRect().left - row.getBoundingClientRect().left;
            row.scrollLeft += offset - (row.clientWidth - card.offsetWidth) / 2;
        }
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        card.scrollIntoView({ block: "nearest", inline: "nearest", behavior: reduce ? "auto" : "smooth" });
    }, [pinned]);

    const highlighted = preview || pinned;
    const domains = data?.domains || [];
    const months = data?.months || [];
    const observed = months.filter((m) => m.count > 0).length;
    const hasData = domains.some((d) => d.assessment?.status && d.assessment.status !== "none");
    const [methodLead, methodText] = METHOD[audience] || METHOD.employer;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%", mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <Box
                component="section"
                aria-labelledby="pearli-trends-title"
                sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: "16px 8px", borderRadius: 2, bgcolor: "#F1F5F9" }}
            >
                <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px 20px", flexWrap: "wrap", px: 1 }}>
                    <Box>
                        <Typography
                            id="pearli-trends-title"
                            component="h2"
                            sx={{ color: "#020618", fontSize: { xs: 20, sm: 24 }, fontWeight: 600, lineHeight: { xs: "32px", sm: "40px" } }}
                        >
                            PEARLI Domain Trends
                        </Typography>
                        <Typography sx={{ color: "#64748B", fontSize: 12, lineHeight: "24px" }}>
                            {data
                                ? `Monthly average for each domain · ${rangeText(data.range.start, data.range.end)} · ${observed} of ${months.length} month${months.length === 1 ? "" : "s"} observed`
                                : "Monthly average for each domain"}
                        </Typography>
                    </Box>
                    <Legend />
                </Box>

                <Box
                    ref={scrollRef}
                    tabIndex={0}
                    role="region"
                    aria-label="Six domain graphs in one row; scroll sideways when needed"
                    aria-describedby={overflowing ? "pearli-trends-scroll-hint" : undefined}
                    sx={{
                        overflowX: "auto",
                        overscrollBehaviorX: "contain",
                        p: "0 8px 4px",
                        scrollbarWidth: "thin",
                        "&:focus-visible": { outline: "3px solid #0080d3", outlineOffset: -3 },
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "repeat(6, minmax(200px, 1fr))", sm: "repeat(6, minmax(176px, 1fr))" },
                            gap: 1,
                        }}
                    >
                        {loading && !data
                            ? Array.from({ length: 6 }, (_, i) => (
                                  <Skeleton key={i} variant="rounded" height={318} sx={{ borderRadius: 2 }} />
                              ))
                            : domains.map((domain) => (
                                  <DomainTrendChart
                                      key={domain.id}
                                      ref={(node) => {
                                          cardRefs.current[domain.id] = node;
                                      }}
                                      domain={domain}
                                      months={months}
                                      countLabel={data.countLabel}
                                      minParticipants={data.rules?.minParticipants}
                                      highlighted={highlighted === domain.id}
                                      dimmed={Boolean(highlighted) && highlighted !== domain.id}
                                  />
                              ))}
                    </Box>
                </Box>
                {overflowing && (
                    <Typography id="pearli-trends-scroll-hint" sx={{ color: "#64748B", fontSize: 11, mx: 2 }}>
                        Scroll sideways to see all six domains.
                    </Typography>
                )}
                <Typography component="div" sx={{ display: "flex", gap: 1, alignItems: "flex-start", px: 1, fontSize: 11, color: "#64748B" }}>
                    <InfoOutlinedIcon sx={{ fontSize: 14, mt: "1px" }} />
                    <span>
                        Each graph shows one domain’s monthly average on the app’s 0–5 scale. The colour shows the
                        domain’s status. Missing months leave gaps. Hover or tap a graph for a month’s value.
                    </span>
                </Typography>
            </Box>

            <Typography sx={{ fontSize: 12, lineHeight: 1.6, color: "#64748B", maxWidth: 1000, mx: 0.25 }}>
                <Box component="strong" sx={{ color: "#475569", fontWeight: 600 }}>{methodLead}</Box> {methodText}
            </Typography>

            <TrendsOverview
                overview={overview}
                refreshing={loading || overviewLoading}
                hasData={hasData}
                domains={domains}
                pinned={pinned}
                onPreview={setPreview}
                onPin={pin}
            />
        </Box>
    );
}
